import { useCallback, useEffect, useState } from 'react';

import { CaseAdapter } from '@shared/CaseAdapter';
import CountriesAPI from '../../../../api/countries_api';
import { Country } from './CountryField';

type UseCountriesResult = {
  countries: Country[];
  isLoading: boolean;
  // An empty `countries` list is otherwise indistinguishable from "the fetch is still loading" or
  // "this démarche genuinely has none" — callers (AntsFormStep's Yup schema) need to tell a fetch
  // failure apart from both, since it would otherwise reject every value forever.
  hasError: boolean;
  retry: () => void;
};

// Fetched once here rather than inside CountryField, so both `nationalite` and `pays_naissance`
// share a single request instead of one each.
const useCountries = (): UseCountriesResult => {
  const [countries, setCountries] = useState<Country[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let cancelled = false;

    setIsLoading(true);
    setHasError(false);

    // France included: this feeds the civil-status nationality/birth-country fields, which need the
    // full controlled vocabulary, not the exchange lookup's answers (spec §5.7 rule A treats "French"
    // as a real value the identity-document rule branches on).
    CountriesAPI.search(undefined, undefined, true).then(({ data }) => {
      if (cancelled) {
        return;
      }
      const { countries: fetchedCountries } = CaseAdapter.toFrontFormat(data) as { countries: Country[] };
      setCountries(fetchedCountries);
      setIsLoading(false);
    }).catch((error) => {
      if (cancelled) {
        return;
      }
      console.error(error);
      setHasError(true);
      setIsLoading(false);
    });

    return () => { cancelled = true; };
  }, [attempt]);

  const retry = useCallback(() => setAttempt((current) => current + 1), []);

  return {
    countries, isLoading, hasError, retry,
  };
};

export { useCountries };
